import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../api';
import { formatPln } from '../catalog/money';
import { milliToDays } from '../pricing/units';
import { formatDecimal } from '../ui/numbers';
import { stateText } from './VersionBar';
import { QUOTES_KEY, type CompareRow, type VersionSummary } from './types';
import { DialogLayer } from '../ui/Dialog';

/**
 * "Porównaj wersje": two versions of the quote and only the positions that differ (added, removed, another
 * quantity or amount). Amounts only for whoever may see the lines' "Kwota netto" (the server leaves them out).
 */
export function CompareDialog({ versions, shownId, onClose }: { versions: VersionSummary[]; shownId: string; onClose: () => void }) {
  const at = Math.max(0, versions.findIndex((v) => v.id === shownId));
  // by default: the shown version against the one before it (or the next, for v1)
  const [b, setB] = useState(versions[at === 0 ? Math.min(1, versions.length - 1) : at]!.id);
  const [a, setA] = useState(versions[at === 0 ? 0 : at - 1]!.id);
  const diff = useQuery({ queryKey: [...QUOTES_KEY, 'compare', a, b], enabled: a !== b, queryFn: () => api<CompareRow[]>(`/api/quotes/versions/${a}/compare/${b}`) });
  const num = (id: string) => versions.find((v) => v.id === id)?.number ?? 0;
  const amounts = (diff.data ?? []).some((r) => r.netA !== undefined);
  const picker = (label: string, value: string, set: (id: string) => void) => (
    <label className="field">
      <span>{label}</span>
      <select aria-label={label} value={value} onChange={(e) => set(e.target.value)}>
        {versions.map((v) => (
          <option key={v.id} value={v.id}>
            v{v.number} · {stateText(v)}
          </option>
        ))}
      </select>
    </label>
  );
  const change = (r: CompareRow) =>
    r.quantityA === 0 && r.daysMilliA === 0 ? 'dodana' : r.quantityB === 0 && r.daysMilliB === 0 ? 'usunięta' : [r.quantityA !== r.quantityB && 'inna ilość', r.daysMilliA !== r.daysMilliB && 'inne dni'].filter(Boolean).join(', ') || 'inna kwota';
  // "2" or, when the days differ, "2 × 1,5 dnia"
  const qty = (q: number, days: number, r: CompareRow) => (r.daysMilliA !== r.daysMilliB && days > 0 ? `${formatDecimal(q)} × ${milliToDays(days)} dn.` : formatDecimal(q));
  return (
    <div className="dialog-backdrop">
      <div className="dialog panel q-compare" role="dialog" aria-modal="true" aria-labelledby="compare-title" data-feature-id="quotes.compare">
        <DialogLayer onClose={onClose} />
        <h2 id="compare-title">Porównaj wersje</h2>
        <div className="q-compare-pick">
          {picker('Wersja', a, setA)}
          {picker('z wersją', b, setB)}
        </div>
        {a === b && <p className="muted">Wybierz dwie różne wersje.</p>}
        {diff.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
        {diff.error && <p className="alert" role="alert">{diff.error instanceof ApiError ? diff.error.message : 'Nie udało się porównać wersji'}</p>}
        {a !== b && diff.data && diff.data.length === 0 && <p>Wersje v{num(a)} i v{num(b)} niczym się nie różnią.</p>}
        {a !== b && diff.data && diff.data.length > 0 && (
          <div className="table-wrap">
            <table className="table" aria-label={`Różnice v${num(a)} i v${num(b)}`}>
              <thead>
                <tr>
                  <th scope="col">Pozycja</th>
                  <th scope="col" className="num">
                    Ilość v{num(a)}
                  </th>
                  <th scope="col" className="num">
                    Ilość v{num(b)}
                  </th>
                  {amounts && (
                    <th scope="col" className="num">
                      Kwota v{num(a)}
                    </th>
                  )}
                  {amounts && (
                    <th scope="col" className="num">
                      Kwota v{num(b)}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {diff.data.map((r) => (
                  <tr key={r.key}>
                    <td>
                      {r.name}
                      <span className="q-kind">{change(r)}</span>
                    </td>
                    <td className="num">{qty(r.quantityA, r.daysMilliA, r)}</td>
                    <td className="num">{qty(r.quantityB, r.daysMilliB, r)}</td>
                    {amounts && <td className="num">{r.netA !== undefined ? formatPln(r.netA) : ''}</td>}
                    {amounts && <td className="num">{r.netB !== undefined ? formatPln(r.netB) : ''}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="actions">
          <button className="btn" type="button" onClick={onClose} autoFocus>
            Zamknij
          </button>
        </div>
      </div>
    </div>
  );
}
